/**
 * MiniGraphPanel — neighbors-only dependency subgraph rendered alongside the
 * Designer's editor surface. Opt-in (toggled by `MiniGraphToggleButton`);
 * pulls from the same `DependencyAnnotations` payload the always-on sidebar
 * already consumes, so it lights up without an extra IPC roundtrip.
 *
 * Layout: a vertical stack sized for the Designer's 256px sidebar rail —
 * "used by" dependents (incoming) on top, the center node (the edited
 * object) in the middle, "references" (outgoing) below, and the "Open in
 * Dependency Viewer" hand-off as a full-width button at the bottom. Each row
 * carries a kind-colored dot so the Designer user can spot a dependent view
 * next to a dependent routine at a glance; truncated names keep their full
 * `schema.name` in a `title` tooltip.
 *
 * The panel intentionally does NOT mount React Flow — the Dependency Viewer
 * already owns that surface and its bundle delta is meaningful. A neighbors-
 * only subgraph only needs a 1-hop list with arrows, and the DOM layout is
 * faster + jsdom-testable.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice H3.
 */
import { ArrowDown, ListTree, Maximize2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import type {
  DependencyAnnotations,
  DependencyItem,
  DependencyObjectType,
} from '../../dependencies/types';

export const MINI_GRAPH_PANEL_TESTID = 'mini-graph-panel';
export const MINI_GRAPH_OPEN_IN_DV_TESTID = 'mini-graph-open-in-dv';

export interface MiniGraphPanelProps {
  readonly annotations: DependencyAnnotations;
  /** Center node — the object whose neighbors the panel shows. */
  readonly center: {
    readonly schema: string | null;
    readonly objectName: string | null;
    readonly kindLabel: string;
  };
  /** Optional handler for "Open in Dependency Viewer" hand-off. When omitted
   *  the button still renders but is disabled. */
  readonly onOpenInDependencyViewer?: () => void;
  /** Optional handler invoked when a neighbor row is activated — used by the
   *  Designer host for the same "Jump to" UX exposed by BlastRadiusPanel. */
  readonly onJumpToNeighbor?: (item: DependencyItem) => void;
  readonly testId?: string;
}

export function MiniGraphPanel({
  annotations,
  center,
  onOpenInDependencyViewer,
  onJumpToNeighbor,
  testId = MINI_GRAPH_PANEL_TESTID,
}: MiniGraphPanelProps) {
  const incoming = annotations.incoming;
  const outgoing = annotations.outgoing;
  const fullName = center.schema
    ? `${center.schema}.${center.objectName ?? '(unnamed)'}`
    : center.objectName ?? '(unnamed)';

  return (
    <div
      className="flex flex-col gap-3 rounded-md border border-border bg-card/40 p-3 text-xs"
      data-testid={testId}
      data-incoming-count={incoming.length}
      data-outgoing-count={outgoing.length}
    >
      <div className="flex items-center gap-1.5 whitespace-nowrap font-medium uppercase tracking-wide text-muted-foreground">
        <ListTree className="size-3.5 shrink-0" />
        Mini-graph
      </div>

      <div className="flex flex-col gap-2">
        <NeighborColumn
          heading="Used by"
          items={incoming}
          onJump={onJumpToNeighbor}
          emptyHint="No incoming dependents"
          testIdSuffix="incoming"
          panelTestId={testId}
        />
        <CenterCell name={fullName} kindLabel={center.kindLabel} testId={`${testId}-center`} />
        <NeighborColumn
          heading="References"
          items={outgoing}
          onJump={onJumpToNeighbor}
          emptyHint="No outgoing references"
          testIdSuffix="outgoing"
          panelTestId={testId}
        />
      </div>

      {annotations.limitedSupport ? (
        <p
          className="text-[10px] italic text-muted-foreground"
          data-testid={`${testId}-limited-support`}
        >
          Engine reports limited dependency tracking — neighbors may be incomplete.
        </p>
      ) : null}

      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-7 w-full gap-1.5 text-[11px]"
        onClick={onOpenInDependencyViewer}
        disabled={!onOpenInDependencyViewer}
        data-testid={MINI_GRAPH_OPEN_IN_DV_TESTID}
      >
        <Maximize2 className="size-3.5" />
        Open in Dependency Viewer
      </Button>
    </div>
  );
}

interface NeighborColumnProps {
  readonly heading: string;
  readonly items: readonly DependencyItem[];
  readonly emptyHint: string;
  readonly onJump?: (item: DependencyItem) => void;
  readonly testIdSuffix: string;
  readonly panelTestId: string;
}

function NeighborColumn({
  heading,
  items,
  emptyHint,
  onJump,
  testIdSuffix,
  panelTestId,
}: NeighborColumnProps) {
  return (
    <div
      className="flex min-w-0 flex-col gap-1"
      data-testid={`${panelTestId}-${testIdSuffix}`}
    >
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground">
        {/* Edges run top-down: dependents → edited object → references. */}
        <ArrowDown className="size-3 shrink-0" />
        {heading}
      </div>
      {items.length === 0 ? (
        <p className="text-[10px] italic text-muted-foreground">{emptyHint}</p>
      ) : (
        <ul className="flex flex-col gap-0.5">
          {items.slice(0, 8).map((item, idx) => (
            <NeighborRow
              key={`${item.schema}-${item.name}-${idx}`}
              item={item}
              onJump={onJump}
            />
          ))}
          {items.length > 8 ? (
            <li className="text-[10px] italic text-muted-foreground">
              + {items.length - 8} more
            </li>
          ) : null}
        </ul>
      )}
    </div>
  );
}

interface NeighborRowProps {
  readonly item: DependencyItem;
  readonly onJump?: (item: DependencyItem) => void;
}

function NeighborRow({ item, onJump }: NeighborRowProps) {
  const fullName = item.schema ? `${item.schema}.${item.name}` : item.name;
  const className =
    'flex min-w-0 items-center gap-1 rounded px-1 py-0.5 text-[10px] font-mono hover:bg-muted';
  if (!onJump) {
    return (
      <li className={className} title={fullName}>
        <KindDot kind={item.objectType} />
        <span className="truncate">{fullName}</span>
      </li>
    );
  }
  return (
    <li>
      <button
        type="button"
        className={`${className} w-full text-left`}
        title={fullName}
        onClick={() => onJump(item)}
      >
        <KindDot kind={item.objectType} />
        <span className="truncate">{fullName}</span>
      </button>
    </li>
  );
}

interface CenterCellProps {
  readonly name: string;
  readonly kindLabel: string;
  readonly testId: string;
}

function CenterCell({ name, kindLabel, testId }: CenterCellProps) {
  return (
    <div
      className="flex w-full min-w-0 flex-col items-center justify-center rounded-md border border-primary/40 bg-primary/10 px-2 py-1.5"
      data-testid={testId}
      title={name}
    >
      <span className="text-[9px] uppercase tracking-wide text-primary/80">
        {kindLabel}
      </span>
      <span className="max-w-full truncate font-mono text-[10px] font-medium text-primary">
        {name}
      </span>
    </div>
  );
}

const KIND_COLORS: Record<DependencyObjectType, string> = {
  table: 'bg-blue-500',
  view: 'bg-green-500',
  procedure: 'bg-orange-500',
  function: 'bg-purple-500',
  trigger: 'bg-yellow-500',
  index: 'bg-zinc-400',
  constraint: 'bg-zinc-400',
  synonym: 'bg-pink-500',
  unknown: 'bg-zinc-400',
};

function KindDot({ kind }: { kind: DependencyObjectType }) {
  return (
    <span
      className={`h-1.5 w-1.5 flex-shrink-0 rounded-full ${KIND_COLORS[kind] ?? 'bg-zinc-400'}`}
      aria-hidden="true"
    />
  );
}
